import { useContext, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { AuthContext } from "../../context/AuthContext";
import {
  Card,
  Row,
  Col,
  Typography,
  Tag,
  Table,
  Divider,
  Space,
  Button,
  Input,
  Modal,
  Spin,
  Alert,
  message,
} from "antd";
import { CheckOutlined, CloseOutlined, ArrowLeftOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

const { Title, Text } = Typography;
const { TextArea } = Input;

const FINAL_APPROVAL_DETAIL_PATH = (id) => `/api/exitprocess/${id}/final-approval/`;
const FINAL_APPROVAL_ACTION_PATH = (id) => `/api/exitprocess/${id}/final-approval/action/`;

const STATUS_COLOR = { PENDING: "gold", APPROVED: "green", REJECTED: "red" };

function employeeName(emp) {
  if (!emp) return "—";
  return [emp.first_name, emp.last_name].filter(Boolean).join(" ").trim() || emp.emp_code;
}

function fmtDate(d) {
  return d ? dayjs(d).format("DD MMM YYYY") : "—";
}

export default function FinalApprovalDetail() {
  const { exitProcessId } = useParams();
  const navigate = useNavigate();
  const { authData, backUrl } = useContext(AuthContext);
  const [messageApi, contextHolder] = message.useMessage();

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [actioning, setActioning] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectRemarks, setRejectRemarks] = useState("");

  function loadData() {
    setLoading(true);
    axios
      .get(`${backUrl}${FINAL_APPROVAL_DETAIL_PATH(exitProcessId)}`, {
        headers: { Authorization: `Bearer ${authData?.access}` },
      })
      .then((res) => setData(res.data))
      .catch((err) => {
        console.error("Error loading final approval detail:", err);
        messageApi.error(
          err.response?.status === 404
            ? "This exit process hasn't been submitted for final approval yet."
            : "Could not load this request."
        );
      })
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    if (!authData?.access || !exitProcessId) return;
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authData?.access, exitProcessId]);

  async function handleAction(action, remarks) {
    setActioning(true);
    try {
      const res = await axios.post(
        `${backUrl}${FINAL_APPROVAL_ACTION_PATH(exitProcessId)}`,
        { action, remarks },
        { headers: { Authorization: `Bearer ${authData?.access}` } }
      );
      setData(res.data);
      messageApi.success(action === "APPROVE" ? "Request approved." : "Request rejected.");
      setRejectOpen(false);
      setRejectRemarks("");
    } catch (err) {
      console.error("Error actioning final approval:", err);
      messageApi.error(err.response?.data?.detail || "Could not save the decision.");
    } finally {
      setActioning(false);
    }
  }

  if (loading) {
    return (
      <div style={{ padding: 48, textAlign: "center" }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!data) {
    return (
      <div style={{ padding: 24 }}>
        {contextHolder}
        <Alert type="warning" showIcon message="Nothing to show for this request." />
      </div>
    );
  }

  const finalApproval = data.final_approval;
  const isPending = finalApproval?.status === "PENDING";

  const itemColumns = (cols) => cols.map((c) => ({ title: c.title, dataIndex: c.key, key: c.key }));

  return (
    <div style={{ padding: 24, maxWidth: 960, margin: "0 auto" }}>
      {contextHolder}
      <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)} style={{ marginBottom: 16 }}>
        Back to approvals
      </Button>

      <Card>
        <Row justify="space-between" align="top">
          <Col>
            <Title level={4} style={{ marginBottom: 4 }}>
              {employeeName(data.employee)}
            </Title>
            <Text type="secondary">
              {data.employee?.emp_code} · Exit clearance for final approval
            </Text>
          </Col>
          <Col>
            <Tag color={STATUS_COLOR[finalApproval?.status] || "default"} style={{ fontSize: 14, padding: "4px 12px" }}>
              {finalApproval?.status}
            </Tag>
          </Col>
        </Row>

        <Divider />

        <Row gutter={24}>
          <Col span={12}>
            <Text strong>Resignation Date: </Text>
            <Text>{fmtDate(data.resignation_date)}</Text>
          </Col>
          <Col span={12}>
            <Text strong>Relieving Date: </Text>
            <Text>{fmtDate(data.relieving_date)}</Text>
          </Col>
        </Row>
        <Row gutter={24} style={{ marginTop: 8 }}>
          <Col span={12}>
            <Text strong>Submitted By: </Text>
            <Text>{employeeName(finalApproval?.submitted_by)}</Text>
          </Col>
          <Col span={12}>
            <Text strong>Submitted At: </Text>
            <Text>{finalApproval?.submitted_at ? dayjs(finalApproval.submitted_at).format("DD MMM YYYY, h:mm A") : "—"}</Text>
          </Col>
        </Row>

        {finalApproval?.status !== "PENDING" && (
          <Row gutter={24} style={{ marginTop: 8 }}>
            <Col span={12}>
              <Text strong>Decided By: </Text>
              <Text>{employeeName(finalApproval?.decided_by)}</Text>
            </Col>
            <Col span={12}>
              <Text strong>Decided At: </Text>
              <Text>{finalApproval?.decided_at ? dayjs(finalApproval.decided_at).format("DD MMM YYYY, h:mm A") : "—"}</Text>
            </Col>
            {finalApproval?.remarks && (
              <Col span={24} style={{ marginTop: 8 }}>
                <Text strong>Remarks: </Text>
                <Text>{finalApproval.remarks}</Text>
              </Col>
            )}
          </Row>
        )}

        <Divider orientation="left">Department Head Clearance</Divider>
        <SectionSummary clearance={data.department_head_clearance} />
        <Table
          size="small"
          rowKey={(r) => r.id ?? r.sr_no}
          pagination={false}
          dataSource={data.department_head_clearance?.items || []}
          columns={itemColumns([
            { title: "Sr.No", key: "sr_no" },
            { title: "Particulars", key: "particulars" },
            { title: "Status", key: "status" },
          ])}
        />
        <Row style={{ marginTop: 8 }}>
          <Col span={24}>
            <Text strong>Handed Over To: </Text>
            <Text>{employeeName(data.department_head_clearance?.handed_over_to)}</Text>
          </Col>
        </Row>

        <Divider orientation="left">IT Clearance</Divider>
        <SectionSummary clearance={data.it_clearance} />
        <Table
          size="small"
          rowKey={(r) => r.id ?? r.sr_no}
          pagination={false}
          dataSource={data.it_clearance?.items || []}
          columns={itemColumns([
            { title: "Sr.No", key: "sr_no" },
            { title: "Item", key: "particulars" },
            { title: "Status", key: "status" },
            { title: "Remarks", key: "remarks" },
          ])}
        />

        <Divider orientation="left">Finance Clearance</Divider>
        <SectionSummary clearance={data.finance_clearance} />
        <Table
          size="small"
          rowKey={(r) => r.id ?? r.sr_no}
          pagination={false}
          dataSource={data.finance_clearance?.items || []}
          columns={itemColumns([
            { title: "Sr.No", key: "sr_no" },
            { title: "Item", key: "item_name" },
            { title: "Amount to Deduct", key: "amount_to_be_deducted" },
            { title: "Remarks", key: "remarks" },
          ])}
        />
        <Row style={{ marginTop: 8 }}>
          <Col span={24}>
            <Text strong>Total Deduction: </Text>
            <Text>{data.finance_clearance?.total_deduction ?? 0}</Text>
          </Col>
        </Row>

        <Divider orientation="left">HR Clearance</Divider>
        <SectionSummary clearance={data.hr_clearance} />
        <Table
          size="small"
          rowKey={(r) => r.id ?? r.sr_no}
          pagination={false}
          dataSource={data.hr_clearance?.items || []}
          columns={itemColumns([
            { title: "Sr.No", key: "sr_no" },
            { title: "Item", key: "particulars" },
            { title: "Status", key: "status" },
            { title: "Remarks", key: "remarks" },
          ])}
        />
        <Row style={{ marginTop: 8 }}>
          {/* <Col span={24}>
            <Text strong>Exit Interview Completed: </Text>
            <Text>{data.hr_clearance?.exit_interview_completed ? "Yes" : "No"}</Text>
          </Col> */}
        </Row>

        {/* <Divider orientation="left">Correspondence</Divider>
        <Row gutter={24}>
          <Col span={16}>
            <Text strong>Address: </Text>
            <Text>{data.address_for_correspondence || "—"}</Text>
          </Col>
          <Col span={8}>
            <Text strong>Phone: </Text>
            <Text>{data.phone_for_correspondence || "—"}</Text>
          </Col>
        </Row> */}

        {isPending && (
          <>
            <Divider />
            <Space>
              <Button
                type="primary"
                icon={<CheckOutlined />}
                loading={actioning}
                onClick={() => handleAction("APPROVE")}
              >
                Approve
              </Button>
              <Button danger icon={<CloseOutlined />} onClick={() => setRejectOpen(true)}>
                Reject
              </Button>
            </Space>
          </>
        )}
      </Card>

      <Modal
        title="Reject exit clearance"
        open={rejectOpen}
        onCancel={() => setRejectOpen(false)}
        onOk={() => handleAction("REJECT", rejectRemarks)}
        okText="Reject"
        okButtonProps={{ danger: true, loading: actioning, disabled: !rejectRemarks.trim() }}
      >
        <Text>Please provide a reason for rejection:</Text>
        <TextArea
          rows={4}
          style={{ marginTop: 8 }}
          value={rejectRemarks}
          onChange={(e) => setRejectRemarks(e.target.value)}
          placeholder="Reason for rejecting this exit clearance..."
        />
      </Modal>
    </div>
  );
}

function SectionSummary({ clearance }) {
  if (!clearance) return <Text type="secondary">No data.</Text>;
  return (
    <Row gutter={24} style={{ marginBottom: 8 }}>
      <Col>
        <Text strong>Approver: </Text>
        <Text>{employeeName(clearance.approver)}</Text>
      </Col>
      <Col>
        <Text strong>Status: </Text>
        <Tag color={STATUS_COLOR[clearance.status] || "default"}>{clearance.status}</Tag>
      </Col>
      <Col>
        <Text strong>Cleared By: </Text>
        <Text>{employeeName(clearance.cleared_by)}</Text>
      </Col>
    </Row>
  );
}